import { Injectable } from '@angular/core';
import { AuthenticationRequest, AuthenticationResult } from '@feathersjs/authentication';
import authentication, { AuthenticationClient, MemoryStorage } from '@feathersjs/authentication-client';
import { NotAuthenticated } from '@feathersjs/errors';
import feathers, { Application, Params, Service } from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConnectionMode } from 'src/app/common/contracts/connection/connection-mode';
import { Deferred } from 'src/app/common/deferred/deferred';

import { IFeathersService } from './contracts/sync/feathers-service';

/**
 * The Feathers authentication client without its own reconnect handling.
 *
 * Out of the box the client listens for `disconnect` and, on the next `connect`, replays the
 * access token it stored after the last successful `authenticate()`. Replaying is all it can do:
 * the token is an OAuth access token from FusionAuth that expires after a few minutes, and only
 * the `AuthClient` can produce a new one from the refresh token. So the replay works exactly
 * when the reconnect happens while the old token is still valid; after a longer outage it fails
 * with 401, the client resets itself and every service call fails until the next reconnect —
 * nothing refreshes, retries or tells the app what happened.
 *
 * The `SessionController` already has to authenticate this connection with a fresh token for
 * reasons that are not reconnects: the gateway verifies the token on the connection on every
 * call, so it must be renewed before it expires; a 401 must be told apart (stale token → refresh
 * and retry, rejected grant → log out); and the app state (`online`, `degraded` with retries,
 * `loggedOut`) is derived from the outcome. Authenticating after a reconnect is the same method
 * once more. Leaving the library's replay switched on would make two actors authenticate the
 * same socket with different tokens, and whichever finished last decided the token on the
 * connection and in the client's storage.
 *
 * Hence one owner: the connection is authenticated only when
 * `BackendSocketAuthenticationService.authenticate()` is called. What the library's reconnect
 * handler did besides the replay is kept: every service call waits until the reconnected
 * connection has been authenticated. A fresh connection carries no token, and socket.io even
 * flushes the calls that were buffered while offline the moment it connects, so this wait in the
 * hook is the only thing that keeps them from failing with 401. `authenticate()`, `logout()`, the
 * hook that attaches the token to every call and the `authenticated`/`logout` events are
 * untouched.
 */
class ExplicitAuthenticationClient extends AuthenticationClient {
  /** Set on disconnect: the hook holds every call on it until the next `authenticate()` has settled. */
  private _nextAuthentication: Deferred<AuthenticationResult> | undefined;

  public handleSocket(socket: SocketIOClient.Socket): void {
    socket.on('disconnect', () => {
      this._nextAuthentication ??= new Deferred<AuthenticationResult>();
      // a rejection is meant for the held calls; without any it must not surface as unhandled
      this._nextAuthentication.promise.catch(() => undefined);
      this.app.set('authentication', this._nextAuthentication.promise);
    });
  }

  public authenticate(request?: AuthenticationRequest, params?: Params): Promise<AuthenticationResult> {
    const result = super.authenticate(request, params);
    // adopts the outcome: the held calls proceed on success and fail with the library's error otherwise
    this._nextAuthentication?.resolve(result);
    this._nextAuthentication = undefined;
    return result;
  }

  public logout(): Promise<AuthenticationResult | null> {
    if (this._nextAuthentication) {
      // never authenticated since the reconnect: there is nothing to tell the server, and the
      // library's logout would wait for an authentication that is not going to happen
      this._nextAuthentication.reject(new NotAuthenticated('logged out before the connection was authenticated'));
      this._nextAuthentication = undefined;
      return this.reset();
    }
    return super.logout();
  }
}

@Injectable({ providedIn: 'root' })
export class FeathersService implements IFeathersService {
  private _ready = new Deferred<void>();
  public get ready(): Promise<void> {
    return this._ready.promise;
  }

  private _app: Application;
  private _connnectionMode = ConnectionMode.default;
  public get connectionMode(): ConnectionMode {
    return this._connnectionMode;
  }

  public set connectionMode(connectionMode: ConnectionMode) {
    this._connnectionMode = connectionMode;
    this._connectionMode$.next(this._connnectionMode);
  }

  private _connectionMode$ = new BehaviorSubject<ConnectionMode>(ConnectionMode.default);
  public get connectionModeChange(): Observable<ConnectionMode> {
    return this._connectionMode$.asObservable();
  }

  public get app(): Application {
    return this._app;
  }

  public getService<T>(serviceName: string): Service<T> {
    return this._app.service(serviceName);
  }

  public setup(socket: SocketIOClient.Socket) {
    this._app = this.setupFeathersApp(socket);
    if ((window as any).Cypress) {
      window['feathers'] = this._app;
    }
    this._ready.resolve();
  }

  private setupFeathersApp(socket: SocketIOClient.Socket) {
    const app = feathers();
    /* timeout (default: 5000ms) - should be same on websocket proxy on alberta.api.gateway
    The time after which a method call fails and times out.
    This usually happens when calling a service or service method that does not exist.
    https://docs.feathersjs.com/api/client/socketio.html#socketiosocket-options
  */
    app.configure(socketio(socket, { timeout: 60000 }));
    app.configure(
      authentication({
        Authentication: ExplicitAuthenticationClient,
        // The client needs the token only while the process runs (it attaches it to every call);
        // the reconnect replay that would have read it from disk is switched off above. Keeping
        // it in memory means the session has one persisted credential — the OAuth token
        // response — instead of a second copy of the access token in localStorage.
        storage: new MemoryStorage(),
        header: 'Authorization',
        path: '/authentication',
        jwtStrategy: 'fusionAuthJwt',
        entity: 'user',
        service: 'users',
        timeout: 15000,
      } as any)
    );

    return app;
  }
}
